export type Menu = {
  id?: string;
  name: string;
  productsCount: number;
  productsDraftCount: number;
  publishedAt: string;
  haveChanges: boolean;
  countryAlpha2Code: string;
  salesChannelsCount: number;
  internalNote?: string;
  isMenuOverwritesEnabled: boolean;
  currency: string;
};

export type MenuInfo = {
  id?: string;
  name: string;
  countryCode: string;
  internalNote?: string;
  isMenuOverwritesEnabled: boolean;
  currency: string;
};

export type CreateMenuData = {
  organizationKeyName: string;
  data: MenuInfo;
};

export type UpdateMenuData = {
  organizationKeyName: string;
  id: string;
  data: MenuInfo;
};

export type UpdateMenuDraftProducts = {
  organizationKeyName: string;
  id: string;
  data: {
    products: {
      id: string;
      sortKey: string | number;
      appearance?: MenuProductAppearance;
    }[];
  };
};

export type MenuDraftResetOrPublish = {
  organizationKeyName: string;
  id: string;
};

export type PushMenuOnSalesChannelData = {
  organizationKeyName: string;
  salesChannelId: string;
};

export type PushMenuToSalesChannelsData = {
  organizationKeyName: string;
  menuId: string;
};

export type PushMenusToSalesDestinationsData = {
  organizationKeyName: string;
  data: {
    destinations: string[];
  };
};

export type MenuProductAppearance = "regular" | "large";

export type MenuProduct = {
  // Not editable here. Reads return it and the draft-products write replaces
  // the whole list, so it has to be carried along or it is lost on save.
  appearance?: MenuProductAppearance;
  category: {
    description?: string | null;
    id: string;
    name: string;
    sortKey: number;
    title: string;
  };
  fieldIndex?: number;
  id: string;
  imageUrl: string;
  priceDescription: string | null;
  name: string;
  sortKey: number;
  title: string;
  price: {
    amount: number;
    currency: string;
  };
  priceDelivery: {
    amount: number;
    currency: string;
  };
};

export type DeleteMenuData = {
  organizationKeyName: string;
  id: string;
};

export type MenusSuccessResponse = {
  data: Menu[];
};

export type MenuSuccessResponse = {
  data: Menu;
};

export type MenuProductsSuccessResponse = {
  data: MenuProduct[];
};

export type MenuSalesChannelShop = {
  id: string;
  name: string;
  shop: {
    title: string;
    imageUrl: string;
  };
};

export type MenuSalesChannelsSuccessResponse = {
  data: MenuSalesChannelShop[];
};

export type MenuSalesChannel = {
  id: string;
  name: string;
  platform: string;
  shopName: string;
  menuPushable: boolean;
  menus: string[];
};

export type SalesChannelsFromMenuSuccessResponse = {
  data: MenuSalesChannel[];
};

export type CurrentMenus = { id: string; name: string };

export type Destination = {
  id: string;
  imageUrl?: string;
  name: string;
  title?: string;
  countryAlpha2Code?: string;
  currentMenus: CurrentMenus[];
  availableMenusReference?: string;
  parent?: string;
  platform: "Foodora" | "Wolt" | "JET" | "HeapsGo";
  shopType: "physical" | "digital" | null;
  menuPushable: boolean;
};

export type AvailableMenu = {
  id: string;
  name: string;
  haveChanges: boolean;
};

type AvailableMenuReference = {
  id: string;
  menus: AvailableMenu[];
};

export type PublishDestinations = {
  destinations: Destination[];
  availableMenus: AvailableMenuReference[];
};

export type PublishMenusToDestinations = {
  organizationKeyName: string;
  destinations?: Array<{
    id: string;
    menus: string[];
  }>;
};

export type MenuRecommendedProduct = {
  id: string;
  product: {
    description: string;
    id: string;
    name: string;
    imageUrl?: string;
    price: {
      amount: number;
      currency: string;
    };
    title: string;
  };
  sortKey: number;
  type: "add_to_order";
};

export type MenuRecommendedProductsSuccessResponse = {
  data: MenuRecommendedProduct[];
};

export type RecommendedProduct = {
  id: string;
  sortKey: number;
  type: "add_to_order";
};

export type UpdateRecommendedProductsPayload = {
  organizationKeyName?: string;
  menuId?: string;
  recommendedProducts: RecommendedProduct[];
};

export enum OverwriteType {
  CHANGE = "CHANGE",
  ADD = "ADD",
  NONE = "",
}

export type MenuOverwrite = {
  createdAt: string;
  description: string | null;
  id: string;
  name: string;
  products: {
    id: string;
    price: {
      amount: number;
      currency: string;
    } | null;
    priceDelivery: {
      amount: number;
      currency: string;
    } | null;
    title: string | null;
    isHidden: boolean;
    isUnavailable: boolean;
    priceDescription: string | null;
    sortKey: number | null;
    product: {
      id: string;
      description: string;
      name: string;
      imageUrl?: string;
      price: {
        amount: number;
        currency: string;
      };
      priceDelivery: {
        amount: number;
        currency: string;
      } | null;
      sortKey: number;
      title: string;
    };
    type: OverwriteType;
  }[];
  schedules: {
    dayOfWeek: 1 | 2 | 3 | 4 | 5 | 6 | 7;
    fromTime: string;
    id: string;
    toTime: string;
  }[];
};

export type MutateMenuOverwriteProduct = {
  id: string;
  priceValue?: number | string;
  priceValueDelivery?: number | string;
  priceDescription?: string | null;
  title?: string;
  sortKey?: number;
  type: OverwriteType;
  isHidden: boolean;
  isUnavailable: boolean;
};

export enum WeekDay {
  Monday = 1 as any,
  Tuesday = 2 as any,
  Wednesday = 3 as any,
  Thursday = 4 as any,
  Friday = 5 as any,
  Saturday = 6 as any,
  Sunday = 7 as any,
}

export type MenuOverwriteSchedule = {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
};

export type MutateMenuOverwritePayload = {
  description: string | null;
  name: string;
  products: MutateMenuOverwriteProduct[];
  schedules: MenuOverwriteSchedule[];
};

export type MenuCampaign = {
  id: string;
  name: string;
  title: string;
  discountType: "fixed_price" | "percentage_discount";
  discountValue: number;
  activeFrom: string;
  activeTo: string;
  countryAlpha2Code: string;
  currency: string;
  enabled: boolean;
};
